import type {
  Fulfillment,
  StorefrontOrderHistoryDetail,
  StorefrontPaymentMethod,
  StorefrontResponsiveImage,
} from "@emynor/storefront-sdk";

export type Lang = "ar" | "en";
export type Localized = { ar: string; en: string };
export type ResponsiveImageSource = string | StorefrontResponsiveImage;

export interface AppConfig {
  title: Localized;
  logo: ResponsiveImageSource;
  homeHeroBanners: ResponsiveImageSource[];
  dineInMenuImage: ResponsiveImageSource | null;
  onlineMenuImage: ResponsiveImageSource | null;
  heroTitle: Localized;
  heroDescription: Localized;
}

export interface OpeningHours {
  open: string;
  close: string;
  closed?: boolean;
}

export interface Branch {
  id: string;
  timezone: string;
  openNow: boolean;
  nextOpening: { dayOfWeek: number; opensAt: string } | null;
  orderPause: { active: boolean; resumesAt: string | null };
  name: Localized;
  address: Localized;
  phones: string[];
  image: string;
  responsiveImage?: StorefrontResponsiveImage;
  hasPhoto?: boolean;
  openingHours: OpeningHours;
  weeklyHours?: Array<{
    dayOfWeek: number;
    opensAt: string | null;
    closesAt: string | null;
    closed: boolean;
  }>;
  translatedLocales?: Lang[];
  coordinates: { lat: number; lng: number } | null;
  youtubeUrl?: string;
  available: boolean;
  indoorService: boolean;
}

export interface Category {
  id: string;
  name: Localized;
  description: Localized;
  image: string;
  responsiveImage?: StorefrontResponsiveImage;
  orderNumber: number;
}
export interface Addition {
  id: string;
  groupId: string;
  name: Localized;
  price: number;
}
export interface AdditionGroup {
  id: string;
  name: Localized;
  minimum: number;
  maximum: number;
  additions: Addition[];
}
export interface Variation {
  id: string;
  name: Localized;
  description: Localized;
  image?: string;
  responsiveImage?: StorefrontResponsiveImage;
  price: number;
  originalPrice?: number | null;
  branchPrice: number;
  additionGroups: AdditionGroup[];
  additions: Addition[];
}
export type MenuItemType = "simple" | "variable";
export interface MenuItem {
  id: string;
  slug: string;
  categoryId: string;
  name: Localized;
  description: Localized;
  image: string;
  responsiveImage?: StorefrontResponsiveImage;
  type: MenuItemType;
  price: number;
  originalPrice?: number | null;
  nextSaleChangeAt?: string | null;
  branchPrice: number;
  additionGroups: AdditionGroup[];
  additions: Addition[];
  variations: Variation[];
  indoorAvailable: boolean;
  onlineAvailable: boolean;
  featured: boolean;
  orderabilityReason?: import("@emynor/storefront-sdk").StorefrontProductOrderabilityReason;
}

export interface DeliveryArea {
  id: string;
  branchId: string;
  name: Localized;
  available: boolean;
  fee: number;
  minimumOrder: number;
  center: { lat: number; lng: number };
  boundary: Record<string, unknown>;
}

export interface Address {
  id: string;
  label: Localized;
  fullName: string;
  phone: string;
  street: string;
  area: string;
  building: string;
  floor: string;
  apartment: string;
  landmark: string;
  note?: string;
  lat: number;
  lng: number;
}
export interface User {
  id: string;
  name: string;
  phone: string;
  verified: boolean;
  addresses: Address[];
}

export type OrderType = "delivery" | "pickup";
export interface OrderTypeSelection {
  type: OrderType;
  branchId: string;
  fulfillment: Fulfillment;
  deliveryLocation?: { lat: number; lng: number };
  deliveryAddress?: Address | null;
  deliveryFee?: number;
  recipientName?: string;
  pickupTimeISO?: string;
}
export interface CartLine {
  id: string;
  itemId: string;
  slug: string;
  name: Localized;
  image: string;
  variationId?: string;
  variationName?: Localized;
  unitPrice: number;
  originalUnitPrice?: number | null;
  additions: Addition[];
  note?: string;
  quantity: number;
}
export interface Cart {
  lines: CartLine[];
  orderType?: OrderTypeSelection;
  redeemLoyaltyPoints?: number;
  promotionCode?: string;
  promotionAttribution?: CartPromotionAttribution;
  linkedPromotionAttribution?: CartPromotionAttribution & { source: "link" };
}
export interface CartPromotionAttribution {
  token: string;
  expiresAt: string;
  promotionId: string;
  promotionName: Localized;
  source: "link" | "code";
}
export type PaymentMethod = "cash" | "online";
export type OrderStatus =
  import("@emynor/storefront-sdk").OrderStatus | "unknown";
export interface OrderPromotion {
  id: string;
  name: Localized;
  savings: number;
}
export interface Order {
  id: string;
  number: string;
  status: OrderStatus;
  createdAt: string;
  type: OrderType;
  branchId: string;
  lines: CartLine[];
  subtotal: number;
  deliveryFee: number;
  serviceFee: number;
  discount: number;
  tax: number;
  total: number;
  currency: string;
  paymentMethod: PaymentMethod;
  paymentStatus:
    | "unpaid"
    | "paid"
    | "processing"
    | "partially_refunded"
    | "refunded"
    | "review_required"
    | "expired"
    | "unknown";
  promotions: OrderPromotion[];
  loyalty: import("@emynor/storefront-sdk").StorefrontOrder["loyalty"];
  deliveryAddress?: Address;
  recipientName?: string;
  pickupTimeISO?: string;
  cancellationReason?: string;
  raw?: StorefrontOrderHistoryDetail;
}
export interface Paginated<T> {
  data: T[];
  page: number;
  limit: number;
  total: number;
}

export function uiPaymentMethod(
  method: StorefrontPaymentMethod,
): PaymentMethod {
  return method === "online" ? "online" : "cash";
}
